import { useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { TZDate } from "@date-fns/tz";
import { format } from "date-fns";
import { Check, Copy } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { InnovaAlgoTrade } from "@/client-admin/types.gen";
import { DataTableColumnHeader } from "../UserTable/data-table-column-header";

function CopyCell({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-1">
      <span className="font-mono text-xs">{value}</span>
      <Button
        variant="ghost"
        size="icon"
        className="h-5 w-5 shrink-0"
        onClick={() => {
          navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      </Button>
    </div>
  );
}

const statusStyles: Record<string, string> = {
  ACTIVE: "bg-green-100 text-green-800",
  TRADED: "bg-blue-100 text-blue-800",
  PARTIALLY_FILLED: "bg-yellow-100 text-yellow-800",
  CANCELLED: "bg-gray-100 text-gray-800",
  PASSED: "bg-gray-100 text-gray-600",
  WITHDRAWN: "bg-red-100 text-red-800",
  NOT_FOUND: "bg-red-100 text-red-800",
};

export const columns: ColumnDef<InnovaAlgoTrade>[] = [
  {
    id: "Contract",
    accessorFn: (row) => `${row.Start}–${row.End}`,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Contract" />,
    cell: ({ row }) => <div className="whitespace-nowrap">{row.getValue("Contract")}</div>,
    filterFn: (row, id, value) => value.includes(row.getValue(id) as string),
    size: 110,
  },
  {
    id: "Side",
    accessorKey: "Side",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Side" />,
    cell: ({ row }) => {
      const side = row.getValue("Side") as string;
      return (
        <Badge variant="outline" className={side === "Buy" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
          {side}
        </Badge>
      );
    },
    filterFn: (row, id, value) => value.includes(row.getValue(id) as string),
    size: 70,
  },
  {
    accessorKey: "SliceId",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Slice" />,
    cell: ({ row }) => <div className="font-mono text-xs">{row.getValue("SliceId")}</div>,
    size: 90,
  },
  {
    accessorKey: "TargetTime",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Target Time" />,
    cell: ({ row }) => {
      const value = row.getValue("TargetTime") as string;
      return <div className="whitespace-nowrap">{format(new TZDate(value, "Europe/Amsterdam"), "HH:mm:ss")}</div>;
    },
    size: 90,
  },
  {
    accessorKey: "OrderVolume",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Volume" />,
    cell: ({ row }) => <div className="whitespace-nowrap">{row.getValue("OrderVolume")} MW</div>,
    size: 75,
  },
  {
    accessorKey: "PriceLimit",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Limit" />,
    cell: ({ row }) => <div className="whitespace-nowrap">{row.getValue("PriceLimit")}</div>,
    size: 70,
  },
  {
    id: "Status",
    accessorFn: (row) => row.Status ?? "PENDING",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    cell: ({ row }) => {
      const status = row.getValue("Status") as string;
      const style = statusStyles[status] ?? "bg-gray-50 text-gray-500";
      return (
        <Badge variant="outline" className={style}>
          {status}
        </Badge>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id) as string);
    },
    size: 120,
  },
  {
    accessorKey: "OrderId",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Order ID" />,
    cell: ({ row }) => {
      const value = row.getValue("OrderId") as string | null;
      if (!value) return <div className="text-muted-foreground">&ndash;</div>;
      return <CopyCell value={value} />;
    },
  },
  {
    accessorKey: "FilledVolume",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Filled" />,
    cell: ({ row }) => {
      const value = row.getValue("FilledVolume") as string | null;
      if (!value) return <div className="text-muted-foreground">&ndash;</div>;
      return <div className="whitespace-nowrap">{value} MW</div>;
    },
    size: 75,
  },
  {
    accessorKey: "FilledPrice",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Fill Price" />,
    cell: ({ row }) => {
      const value = row.getValue("FilledPrice") as string | null;
      if (!value) return <div className="text-muted-foreground">&ndash;</div>;
      return <div className="whitespace-nowrap">{value}</div>;
    },
    size: 80,
  },
];
